import { Helmet } from "react-helmet";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { CheckCircle2, Phone, Clock, Zap, Globe, BarChart, Shield } from "lucide-react";
import { Link } from "react-router-dom";
import { SharedHeader } from "@/components/SharedHeader";

const AICallCenter = () => {
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "What is a 24/7 AI call center?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "A 24/7 AI call center uses artificial intelligence to handle customer phone calls around the clock without human agents. It answers questions, books appointments, captures leads, and routes complex calls to humans — all automatically."
        }
      },
      {
        "@type": "Question",
        name: "How much does a 24/7 AI call center cost?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "AI call center software starts at $49/month with Call24x7.AI, compared to $35,000+/year per human agent. Most businesses save 70-85% on customer support costs by switching to AI."
        }
      },
      {
        "@type": "Question",
        name: "How fast can I set up an AI call center?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "With Call24x7.AI, you can set up a fully functional AI call center in under 60 seconds. Enter your website URL, customize your agent, and start taking calls immediately."
        }
      },
      {
        "@type": "Question",
        name: "Can an AI call center handle multiple languages?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes — Call24x7.AI supports 30+ languages natively, allowing you to serve customers worldwide without hiring multilingual staff."
        }
      },
      {
        "@type": "Question",
        name: "Is a 24/7 AI call center suitable for small businesses?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Absolutely. AI call centers are especially valuable for small businesses that cannot afford 24/7 human staff. You get enterprise-level phone coverage starting at $49/month with no per-minute fees."
        }
      }
    ]
  };

  return (
    <>
      <Helmet>
        <title>24/7 AI Call Center Software — Handle Unlimited Calls | Call24x7.AI</title>
        <meta name="description" content="Build a 24/7 AI call center in 60 seconds. AI answers calls, books appointments & captures leads around the clock. Starting at $49/mo — 70% cheaper than human agents." />
        <meta name="keywords" content="24/7 AI call center, AI call center software, 24 7 AI call center, AI answering service 24/7, automated call center, AI customer support, call 24 answering service" />
        <link rel="canonical" href="https://call24x7.ai/ai-call-center" />
        <meta name="robots" content="index, follow" />

        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://call24x7.ai/ai-call-center" />
        <meta property="og:title" content="24/7 AI Call Center — Automate Every Customer Call" />
        <meta property="og:description" content="Build a 24/7 AI call center in 60 seconds. Starting at $49/mo — 70% cheaper than human agents." />
        <meta property="og:site_name" content="Call24x7.AI" />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            name: "Call24x7.AI — 24/7 AI Call Center",
            description: "AI call center software that handles unlimited customer calls 24/7. Answers questions, books appointments, captures leads automatically.",
            brand: { "@type": "Brand", name: "Call24x7.AI" },
            offers: {
              "@type": "AggregateOffer",
              lowPrice: "49",
              highPrice: "499",
              priceCurrency: "USD",
              offerCount: "3",
              availability: "https://schema.org/InStock"
            },
            aggregateRating: {
              "@type": "AggregateRating",
              ratingValue: "4.9",
              ratingCount: "500",
              bestRating: "5"
            }
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>

      <SharedHeader />

      <main className="min-h-screen bg-background">
        {/* Hero */}
        <section className="pt-32 pb-16 px-4">
          <div className="container mx-auto max-w-4xl text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              Build a <span className="text-primary">24/7 AI Call Center</span> in 60 Seconds
            </h1>
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              AI phone agents that answer every call, book appointments, capture leads, and handle customer support — around the clock, at a fraction of the cost.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <Button size="lg" className="text-lg px-8" onClick={() => window.location.href = "https://manage.call24x7.ai/signup"}>
                Start Free Trial
              </Button>
              <Button size="lg" variant="outline" className="text-lg px-8" asChild>
                <Link to="/#pricing">View Pricing</Link>
              </Button>
            </div>
            <p className="text-sm text-muted-foreground">No credit card required • Setup in 60 seconds • Cancel anytime</p>
          </div>
        </section>

        {/* Stats */}
        <section className="py-12 bg-muted/30">
          <div className="container mx-auto max-w-4xl">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
              <div>
                <p className="text-3xl font-bold text-primary">70%</p>
                <p className="text-sm text-muted-foreground">Cost Savings vs Human Agents</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-primary">24/7</p>
                <p className="text-sm text-muted-foreground">Always Available</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-primary">60s</p>
                <p className="text-sm text-muted-foreground">Setup Time</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-primary">30+</p>
                <p className="text-sm text-muted-foreground">Languages Supported</p>
              </div>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="py-16 px-4">
          <div className="container mx-auto max-w-4xl">
            <h2 className="text-3xl font-bold text-center mb-12">Everything You Need in an AI Call Center</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { icon: Clock, title: "24/7/365 Coverage", desc: "Never miss a call again. Your AI agent works nights, weekends, and holidays without overtime." },
                { icon: Zap, title: "Unlimited Simultaneous Calls", desc: "Handle 1 or 1,000 calls at once. No hold times, no busy signals, no dropped calls." },
                { icon: Phone, title: "Natural Conversations", desc: "Advanced NLP creates human-like interactions. Callers often cannot tell it is AI." },
                { icon: Globe, title: "30+ Languages", desc: "Serve customers worldwide without hiring multilingual staff." },
                { icon: BarChart, title: "Real-Time Analytics", desc: "Track call volume, resolution rates, sentiment, and more in a live dashboard." },
                { icon: Shield, title: "Enterprise Security", desc: "SOC 2 compliant, encrypted calls, and HIPAA-conscious workflows for healthcare." },
              ].map((feature, i) => (
                <Card key={i} className="p-6">
                  <feature.icon className="w-8 h-8 text-primary mb-4" />
                  <h3 className="font-bold mb-2">{feature.title}</h3>
                  <p className="text-sm text-muted-foreground">{feature.desc}</p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Cost comparison */}
        <section className="py-16 px-4 bg-muted/30">
          <div className="container mx-auto max-w-4xl">
            <h2 className="text-3xl font-bold text-center mb-8">AI Call Center vs Traditional Call Center</h2>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse border border-border bg-background">
                <thead className="bg-muted">
                  <tr>
                    <th className="text-left py-3 px-4 border border-border">Feature</th>
                    <th className="text-left py-3 px-4 border border-border">Traditional Call Center</th>
                    <th className="text-left py-3 px-4 border border-border">Call24x7.AI</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td className="py-3 px-4 border border-border font-medium">Cost per agent</td><td className="py-3 px-4 border border-border">$35,000+/yr</td><td className="py-3 px-4 border border-border text-primary font-semibold">$49–199/mo</td></tr>
                  <tr><td className="py-3 px-4 border border-border font-medium">Availability</td><td className="py-3 px-4 border border-border">Business hours (overtime extra)</td><td className="py-3 px-4 border border-border text-primary font-semibold">24/7/365</td></tr>
                  <tr><td className="py-3 px-4 border border-border font-medium">Simultaneous calls</td><td className="py-3 px-4 border border-border">1 per agent</td><td className="py-3 px-4 border border-border text-primary font-semibold">Unlimited</td></tr>
                  <tr><td className="py-3 px-4 border border-border font-medium">Setup time</td><td className="py-3 px-4 border border-border">Weeks to months</td><td className="py-3 px-4 border border-border text-primary font-semibold">60 seconds</td></tr>
                  <tr><td className="py-3 px-4 border border-border font-medium">Training</td><td className="py-3 px-4 border border-border">2–6 weeks per agent</td><td className="py-3 px-4 border border-border text-primary font-semibold">Instant</td></tr>
                  <tr><td className="py-3 px-4 border border-border font-medium">Languages</td><td className="py-3 px-4 border border-border">Hire per language</td><td className="py-3 px-4 border border-border text-primary font-semibold">30+ built-in</td></tr>
                </tbody>
              </table>
            </div>
            <p className="text-center mt-4 text-sm text-muted-foreground">
              See our full <Link to="/blog/ai-vs-human-agents-cost-comparison" className="text-primary underline hover:no-underline">AI vs human agents cost breakdown</Link> for detailed analysis.
            </p>
          </div>
        </section>

        {/* Use cases */}
        <section className="py-16 px-4">
          <div className="container mx-auto max-w-4xl">
            <h2 className="text-3xl font-bold text-center mb-8">Industries Using 24/7 AI Call Centers</h2>
            <div className="grid md:grid-cols-2 gap-4">
              {[
                { name: "Medical Clinics", desc: "HIPAA-ready scheduling, prescription refill requests, patient intake", link: "/industries/medical-clinics" },
                { name: "Real Estate", desc: "Lead capture, property inquiries, showing scheduling 24/7", link: "/industries/real-estate" },
                { name: "Restaurants", desc: "Reservations, takeout orders, menu inquiries on autopilot", link: "/industries/restaurants" },
                { name: "HVAC & Home Services", desc: "Emergency dispatch, appointment booking, service inquiries", link: "/industries/hvac" },
                { name: "Law Firms", desc: "Client intake, consultation scheduling, case status updates", link: "/industries/law-firms" },
                { name: "E-Commerce", desc: "Order status, returns, product inquiries at scale", link: "/industries/ecommerce" },
              ].map((industry, i) => (
                <Link key={i} to={industry.link} className="block">
                  <Card className="p-4 hover:border-primary/50 transition-colors">
                    <h3 className="font-bold mb-1">{industry.name}</h3>
                    <p className="text-sm text-muted-foreground">{industry.desc}</p>
                  </Card>
                </Link>
              ))}
            </div>
            <p className="text-center mt-6">
              <Link to="/industries" className="text-primary underline hover:no-underline">View all 60+ industry solutions →</Link>
            </p>
          </div>
        </section>

        {/* How it works */}
        <section className="py-16 px-4 bg-muted/30">
          <div className="container mx-auto max-w-3xl">
            <h2 className="text-3xl font-bold text-center mb-8">Set Up Your AI Call Center in 3 Steps</h2>
            <div className="space-y-6">
              {[
                { step: "1", title: "Enter Your Website URL", desc: "Our AI analyzes your business and creates a custom phone agent in 60 seconds." },
                { step: "2", title: "Customize & Test", desc: "Fine-tune responses, set business hours, connect your calendar and CRM." },
                { step: "3", title: "Go Live", desc: "Forward your business number or get a new one. Start handling calls 24/7." },
              ].map((item, i) => (
                <div key={i} className="flex gap-4 items-start">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-lg">
                    {item.step}
                  </div>
                  <div>
                    <h3 className="font-bold text-lg">{item.title}</h3>
                    <p className="text-muted-foreground">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 px-4">
          <div className="container mx-auto max-w-3xl">
            <h2 className="text-3xl font-bold text-center mb-8">Frequently Asked Questions</h2>
            <div className="space-y-6">
              <div>
                <h3 className="font-semibold mb-2">What is a 24/7 AI call center?</h3>
                <p className="text-muted-foreground">A 24/7 AI call center uses artificial intelligence to handle customer phone calls around the clock without human agents. It answers questions, books appointments, captures leads, and routes complex calls to humans — all automatically.</p>
              </div>
              <div>
                <h3 className="font-semibold mb-2">How much does a 24/7 AI call center cost?</h3>
                <p className="text-muted-foreground">AI call center software starts at $49/month with Call24x7.AI, compared to $35,000+/year per human agent. Most businesses save 70-85% on customer support costs.</p>
              </div>
              <div>
                <h3 className="font-semibold mb-2">How fast can I set up an AI call center?</h3>
                <p className="text-muted-foreground">With Call24x7.AI, setup takes under 60 seconds. Enter your website URL, customize your agent, and start taking calls immediately.</p>
              </div>
              <div>
                <h3 className="font-semibold mb-2">Can an AI call center handle multiple languages?</h3>
                <p className="text-muted-foreground">Yes — Call24x7.AI supports 30+ languages natively, allowing you to serve customers worldwide without hiring multilingual staff.</p>
              </div>
              <div>
                <h3 className="font-semibold mb-2">Is a 24/7 AI call center suitable for small businesses?</h3>
                <p className="text-muted-foreground">Absolutely. AI call centers are especially valuable for small businesses. Read our <Link to="/blog/ai-receptionist-for-small-business" className="text-primary underline hover:no-underline">AI receptionist guide for small businesses</Link> to learn more.</p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 px-4">
          <div className="container mx-auto max-w-2xl">
            <Card className="bg-gradient-to-r from-primary/10 to-secondary/10 border-primary/20">
              <CardContent className="pt-8 text-center">
                <h2 className="text-2xl font-bold mb-4">Ready to Build Your 24/7 AI Call Center?</h2>
                <p className="text-muted-foreground mb-6">Join 2,000+ businesses using Call24x7.AI to handle every customer call. Free trial, no credit card required.</p>
                <Button size="lg" className="text-lg px-8" onClick={() => window.location.href = "https://manage.call24x7.ai/signup"}>
                  Start Free Trial →
                </Button>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Related content */}
        <section className="py-12 px-4 bg-muted/30">
          <div className="container mx-auto max-w-3xl">
            <h2 className="text-xl font-bold mb-4">Learn More</h2>
            <ul className="space-y-2 text-sm">
              <li>• <Link to="/blog/what-is-ai-answering-service" className="text-primary underline hover:no-underline">What Is an AI Answering Service?</Link></li>
              <li>• <Link to="/blog/ai-vs-human-agents-cost-comparison" className="text-primary underline hover:no-underline">AI vs Human Agents: Full Cost Breakdown</Link></li>
              <li>• <Link to="/blog/best-ai-phone-agent-software" className="text-primary underline hover:no-underline">7 Best AI Phone Agent Platforms Compared</Link></li>
              <li>• <Link to="/blog/how-to-set-up-ai-call-center" className="text-primary underline hover:no-underline">How to Set Up an AI Call Center in 6 Steps</Link></li>
              <li>• <Link to="/blog/reduce-customer-support-costs-with-ai" className="text-primary underline hover:no-underline">How to Reduce Support Costs by 70% with AI</Link></li>
            </ul>
          </div>
        </section>
      </main>
    </>
  );
};

export default AICallCenter;
